import { html } from "lit";
import type { GatewayHelloOk } from "../gateway.ts";
import type { UiSettings } from "../storage.ts";
import { formatRelativeTimestamp, formatDurationHuman } from "../format.ts";
import { formatNextRun } from "../presenter.ts";
import { t } from "../i18n.ts";

export type OverviewProps = {
  connected: boolean;
  hello: GatewayHelloOk | null;
  settings: UiSettings;
  password: string;
  lastError: string | null;
  presenceCount: number;
  sessionsCount: number | null;
  cronEnabled: boolean | null;
  cronNext: number | null;
  lastChannelsRefresh: number | null;
  onSettingsChange: (next: UiSettings) => void;
  onPasswordChange: (next: string) => void;
  onSessionKeyChange: (next: string) => void;
  onConnect: () => void;
  onRefresh: () => void;
};

export function renderOverview(props: OverviewProps) {
  const snapshot = props.hello?.snapshot as
    | {
        uptimeMs?: number;
        policy?: { tickIntervalMs?: number };
        authMode?: "none" | "token" | "password" | "trusted-proxy";
      }
    | undefined;
  const uptime = snapshot?.uptimeMs ? formatDurationHuman(snapshot.uptimeMs) : "n/a";
  const tick = snapshot?.policy?.tickIntervalMs ? `${snapshot.policy.tickIntervalMs}ms` : "n/a";
  const authMode = snapshot?.authMode;
  const isTrustedProxy = authMode === "trusted-proxy";
  const authHint = (() => {
    if (props.connected || !props.lastError) {
      return null;
    }
    const lower = props.lastError.toLowerCase();
    const authFailed = lower.includes("unauthorized") || lower.includes("connect failed");
    if (!authFailed) {
      return null;
    }
    const hasToken = Boolean(props.settings.token.trim());
    const hasPassword = Boolean(props.password.trim());
    if (!hasToken && !hasPassword) {
      return html`
        <div class="muted" style="margin-top: 8px">
          ${t("overview.auth_required")}
          <div style="margin-top: 6px">
            <span class="mono">nabe dashboard --no-open</span> → ${t("overview.auth_hint_dashboard")}<br />
            <span class="mono">nabe doctor --generate-gateway-token</span> → ${t("overview.auth_hint_token")}
          </div>
          <div style="margin-top: 6px">
            <a
              class="session-link"
              href="https://docs.nabe.ai/web/dashboard"
              target="_blank"
              rel="noreferrer"
              title="${t("overview.docs_auth")}"
              >${t("overview.docs_auth")}</a
            >
          </div>
        </div>
      `;
    }
    return html`
      <div class="muted" style="margin-top: 8px">
        ${t("overview.auth_failed")}
        <div style="margin-top: 6px">
          <a
            class="session-link"
            href="https://docs.nabe.ai/web/dashboard"
            target="_blank"
            rel="noreferrer"
            title="${t("overview.docs_auth")}"
            >${t("overview.docs_auth")}</a
          >
        </div>
      </div>
    `;
  })();
  const insecureContextHint = (() => {
    if (props.connected || !props.lastError) {
      return null;
    }
    const isSecureContext = typeof window !== "undefined" ? window.isSecureContext : true;
    if (isSecureContext) {
      return null;
    }
    const lower = props.lastError.toLowerCase();
    if (!lower.includes("secure context") && !lower.includes("device identity required")) {
      return null;
    }
    return html`
      <div class="muted" style="margin-top: 8px">
        ${t("overview.insecure_context")}
        <span class="mono">http://127.0.0.1:18789</span> on the gateway host.
        <div style="margin-top: 6px">
          ${t("overview.insecure_stay")}
          <span class="mono">gateway.controlUi.allowInsecureAuth: true</span> (token-only).
        </div>
        <div style="margin-top: 6px">
          <a
            class="session-link"
            href="https://docs.nabe.ai/gateway/tailscale"
            target="_blank"
            rel="noreferrer"
            title="Tailscale Serve docs (opens in new tab)"
            >${t("overview.docs_tailscale")}</a
          >
          <span class="muted"> · </span>
          <a
            class="session-link"
            href="https://docs.nabe.ai/web/control-ui#insecure-http"
            target="_blank"
            rel="noreferrer"
            title="Insecure HTTP docs (opens in new tab)"
            >${t("overview.docs_insecure")}</a
          >
        </div>
      </div>
    `;
  })();

  return html`
    <!-- Quick Stats at a Glance -->
    <section class="grid grid-cols-3">
      <div class="card stat-card">
        <div class="stat-label">${t("overview.connection")}</div>
        <div class="stat-value ${props.connected ? "ok" : "warn"}">
          ${props.connected ? t("overview.online") : t("overview.offline")}
        </div>
        <div class="muted" style="margin-top: 4px;">
          ${props.connected ? `${t("overview.running_for")} ${uptime}` : t("overview.not_connected")}
        </div>
      </div>
      <div class="card stat-card">
        <div class="stat-label">${t("overview.active_sessions")}</div>
        <div class="stat-value">${props.sessionsCount ?? "0"}</div>
        <div class="muted" style="margin-top: 4px;">${t("overview.sessions_now")}</div>
      </div>
      <div class="card stat-card">
        <div class="stat-label">${t("overview.scheduled_tasks")}</div>
        <div class="stat-value">
          ${props.cronEnabled == null ? t("overview.scheduled_none") : props.cronEnabled ? t("overview.scheduled_active") : t("overview.scheduled_paused")}
        </div>
        <div class="muted" style="margin-top: 4px;">${formatNextRun(props.cronNext)}</div>
      </div>
    </section>

    ${
      props.lastError
        ? html`
      <section class="card" style="margin-top: 20px; border-color: var(--danger-subtle);">
        <div class="card-title" style="color: var(--danger);">${t("overview.attention")}</div>
        <div class="card-sub" style="margin-top: 8px;">${props.lastError}</div>
        ${authHint ?? ""}
        ${insecureContextHint ?? ""}
      </section>
    `
        : ""
    }

    <section class="grid grid-cols-2" style="margin-top: 20px;">
      <!-- Connection Settings -->
      <div class="card">
        <div class="card-title">${t("overview.connection_settings")}</div>
        <div class="card-sub">${t("overview.connection_sub")}</div>
        <div class="form-grid" style="margin-top: 20px;">
          <label class="field">
            <span>${t("overview.server_address")}</span>
            <input
              .value=${props.settings.gatewayUrl}
              @input=${(e: Event) => {
                const v = (e.target as HTMLInputElement).value;
                props.onSettingsChange({ ...props.settings, gatewayUrl: v });
              }}
              placeholder="ws://your-server:18789"
            />
          </label>
          ${
            isTrustedProxy
              ? ""
              : html`
                <label class="field">
                  <span>${t("overview.password")}</span>
                  <input
                    type="password"
                    .value=${props.settings.password || props.password}
                    @input=${(e: Event) => {
                      const v = (e.target as HTMLInputElement).value;
                      props.onPasswordChange(v);
                      props.onSettingsChange({ ...props.settings, password: v });
                    }}
                    placeholder="${t("overview.enter_password")}"
                  />
                </label>
              `
          }
          <label class="field">
            <span>${t("overview.session_name")}</span>
            <input
              .value=${props.settings.sessionKey}
              @input=${(e: Event) => {
                const v = (e.target as HTMLInputElement).value;
                props.onSessionKeyChange(v);
              }}
              placeholder="main"
            />
          </label>
        </div>
        <div class="row" style="margin-top: 18px; gap: 12px;">
          <button class="btn primary" @click=${() => props.onConnect()}>
            ${props.connected ? t("overview.reconnect") : t("overview.connect_btn")}
          </button>
          <button class="btn" @click=${() => props.onRefresh()}>${t("overview.refresh_data")}</button>
        </div>
      </div>

      <!-- System Info -->
      <div class="card">
        <div class="card-title">${t("overview.system_info")}</div>
        <div class="card-sub">${t("overview.system_sub")}</div>
        <div class="stat-grid" style="margin-top: 20px;">
          <div class="stat">
            <div class="stat-label">${t("overview.running_time")}</div>
            <div class="stat-value">${uptime}</div>
          </div>
          <div class="stat">
            <div class="stat-label">${t("overview.connected_devices")}</div>
            <div class="stat-value">${props.presenceCount}</div>
          </div>
          <div class="stat">
            <div class="stat-label">${t("overview.last_refresh")}</div>
            <div class="stat-value" style="font-size: 20px;">
              ${props.lastChannelsRefresh ? formatRelativeTimestamp(props.lastChannelsRefresh) : t("overview.never")}
            </div>
          </div>
        </div>
        ${
          !props.lastError
            ? html`
                <div class="callout" style="margin-top: 18px;">
                  ${t("overview.all_good")}
                </div>
              `
            : ""
        }
      </div>
    </section>

    <!-- Quick Tips -->
    <section class="card" style="margin-top: 20px;">
      <div class="card-title">${t("overview.getting_started")}</div>
      <div class="card-sub">${t("overview.getting_started_sub")}</div>
      <div class="note-grid" style="margin-top: 18px;">
        <div>
          <div class="note-title">${t("overview.tip_connect")}</div>
          <div class="muted">${t("overview.tip_connect_desc")}</div>
        </div>
        <div>
          <div class="note-title">${t("overview.tip_chat")}</div>
          <div class="muted">${t("overview.tip_chat_desc")}</div>
        </div>
        <div>
          <div class="note-title">${t("overview.tip_settings")}</div>
          <div class="muted">${t("overview.tip_settings_desc")}</div>
        </div>
      </div>
    </section>
  `;
}
